import React, { useMemo } from "react";
import { Modal, Pressable, ScrollView, StyleSheet, Text, View } from "react-native";

/** @param {string} r */
function humanStepResult(r) {
  switch (r) {
    case "continue":
      return "Continue";
    case "allow":
      return "Allow";
    case "block":
      return "Restrict";
    case "pass":
      return "Pass";
    default:
      return r;
  }
}

/** @param {string} t */
function humanRuleType(t) {
  switch (t) {
    case "mode":
      return "Mode";
    case "category":
      return "Category";
    case "time":
      return "Schedule";
    case "manual":
      return "Manual override";
    default:
      return t;
  }
}

/** @param {string} r */
function pillStyleForResult(r) {
  switch (r) {
    case "block":
      return styles.pillBlock;
    case "allow":
      return styles.pillAllow;
    case "pass":
      return styles.pillPass;
    case "continue":
    default:
      return styles.pillContinue;
  }
}

/** @param {string} r */
function pillTextStyleForResult(r) {
  switch (r) {
    case "block":
      return styles.pillTextBlock;
    case "allow":
      return styles.pillTextAllow;
    case "pass":
      return styles.pillTextPass;
    case "continue":
    default:
      return styles.pillTextContinue;
  }
}

/**
 * Human-readable visualization of per-app decision traces (prediction layer — not on-device shields).
 *
 * @param {{ visible: boolean, onClose: () => void, simulation: object | null, planMode: string }} props
 */
export function LockboxDebugDashboard({ visible, onClose, simulation, planMode }) {
  const traces = simulation?.decisionTraces || [];
  const explanation = simulation?.explanation || [];

  const sortedTraces = useMemo(() => {
    const copy = [...traces];
    copy.sort((a, b) => {
      if (a.finalDecision !== b.finalDecision) return a.finalDecision === "block" ? -1 : 1;
      return (a.app || "").localeCompare(b.app || "");
    });
    return copy;
  }, [traces]);

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <View style={styles.sheet}>
          <View style={styles.header}>
            <View style={styles.headerTitles}>
              <Text style={styles.title}>App decision debugger</Text>
              <Text style={styles.subtitle}>Why was this restricted? (rule trace)</Text>
            </View>
            <Pressable onPress={onClose} hitSlop={12} accessibilityRole="button">
              <Text style={styles.close}>Close</Text>
            </Pressable>
          </View>

          <ScrollView contentContainerStyle={styles.scroll}>
            <Text style={styles.sectionLabel}>Run context</Text>
            <Text style={styles.meta}>
              Mode: {planMode} · {simulation?.enforcementMode === "production" ? "Production hand-off" : "Sandbox"}{" "}
              · {simulation?.blocked?.length ?? 0} restricted · {simulation?.allowed?.length ?? 0} allowed
            </Text>

            {explanation.length > 0 ? (
              <View style={styles.explanationBox}>
                <Text style={styles.explanationTitle}>Pipeline notes</Text>
                {explanation.map((line, i) => (
                  <Text key={i} style={styles.explanationLine}>
                    {line}
                  </Text>
                ))}
              </View>
            ) : null}

            {sortedTraces.length === 0 ? (
              <View style={styles.emptyBox}>
                <Text style={styles.emptyTitle}>No per-app traces yet</Text>
                <Text style={styles.emptyBody}>
                  Traces appear when the simulator runs with decision tracing on and your plan includes at least one app
                  in the block or allow lists. Add apps in Lockbox lab, then open this debugger again.
                </Text>
              </View>
            ) : null}

            {sortedTraces.map((t) => {
              const restricted = t.finalDecision === "block";
              return (
                <View key={t.appId} style={styles.card}>
                  <Text style={styles.cardSectionLabel}>Final result</Text>
                  <View style={[styles.verdictBanner, restricted ? styles.verdictBlock : styles.verdictAllow]}>
                    <Text style={styles.verdictAppName}>{t.app}</Text>
                    <Text style={styles.verdictOutcome}>{restricted ? "→ Restricted" : "→ Allowed"}</Text>
                    <Text style={styles.verdictHint}>
                      {restricted
                        ? "This app is on the plan’s restrict list after rules were evaluated."
                        : "This app is on the plan’s allow list (or default-allow) after rules were evaluated."}
                    </Text>
                  </View>
                  <Text style={styles.cardSub}>Inventory id: {t.appId}</Text>

                  <Text style={styles.subhead}>Decision path</Text>
                  <Text style={styles.pathHint}>Steps run in order; the first terminal outcome matches the rule engine.</Text>
                  {(t.evaluationSteps || []).map((s) => (
                    <View key={`${t.appId}-${s.step}`} style={styles.stepRow}>
                      <View style={styles.stepIndex}>
                        <Text style={styles.stepIndexText}>{s.step}</Text>
                      </View>
                      <View style={styles.stepBody}>
                        <Text style={styles.stepDescription}>{s.description}</Text>
                      </View>
                      <View style={[styles.pill, pillStyleForResult(s.result)]}>
                        <Text style={[styles.pillText, pillTextStyleForResult(s.result)]}>
                          {humanStepResult(s.result)}
                        </Text>
                      </View>
                    </View>
                  ))}

                  {t.conflictResolution?.length ? (
                    <View style={styles.conflictBox}>
                      <Text style={styles.subheadConflict}>Conflict resolver</Text>
                      {t.conflictResolution.map((c, i) => (
                        <View key={i} style={styles.conflictInner}>
                          <Text style={styles.conflictLabel}>Conflict</Text>
                          <Text style={styles.conflictText}>{c.conflict}</Text>
                          <Text style={styles.conflictWinner}>
                            Winner: <Text style={styles.conflictWinnerEm}>{c.winner}</Text>
                          </Text>
                          <Text style={styles.conflictReason}>{c.reason}</Text>
                        </View>
                      ))}
                    </View>
                  ) : null}

                  <Text style={styles.subhead}>Triggered rules</Text>
                  <Text style={styles.pathHint}>Rules that fired for this app on the way to the final lists.</Text>
                  {(t.triggeredRules || []).map((r) => (
                    <View key={`${t.appId}-${r.ruleId}`} style={styles.ruleRow}>
                      <View style={styles.ruleDot} />
                      <View style={styles.ruleTextCol}>
                        <Text style={styles.ruleName}>{r.ruleName}</Text>
                        <Text style={styles.ruleMeta}>
                          {humanRuleType(r.type)} · <Text style={styles.ruleIdMono}>{r.ruleId}</Text>
                        </Text>
                      </View>
                    </View>
                  ))}
                </View>
              );
            })}

            <View style={styles.disclaimer}>
              <Text style={styles.disclaimerText}>
                Sandbox shows predicted restriction intent from JavaScript rules. On-device shields (Screen Time /
                FamilyControls) are execution: timing, tokens, and OS policy can differ.
              </Text>
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: "rgba(0,0,0,0.45)", justifyContent: "flex-end" },
  sheet: {
    maxHeight: "88%",
    backgroundColor: "#f4f4f5",
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingBottom: 28,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    paddingHorizontal: 18,
    paddingTop: 16,
    paddingBottom: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: "#e4e4e7",
    backgroundColor: "#fafafa",
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  headerTitles: { flex: 1, paddingRight: 12 },
  title: { fontSize: 18, fontWeight: "700", color: "#18181b" },
  subtitle: { fontSize: 13, color: "#71717a", marginTop: 4 },
  close: { fontSize: 16, color: "#2563eb", fontWeight: "600" },
  scroll: { padding: 18, paddingBottom: 36 },
  sectionLabel: {
    fontSize: 12,
    fontWeight: "700",
    color: "#71717a",
    textTransform: "uppercase",
    letterSpacing: 0.6,
    marginBottom: 6,
  },
  meta: { fontSize: 14, color: "#3f3f46", marginBottom: 12, lineHeight: 20 },
  explanationBox: {
    backgroundColor: "#ecfeff",
    borderRadius: 10,
    padding: 12,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "#a5f3fc",
  },
  explanationTitle: { fontSize: 12, fontWeight: "700", color: "#0e7490", marginBottom: 6 },
  explanationLine: { fontSize: 12, color: "#155e75", lineHeight: 18, marginBottom: 4 },
  emptyBox: {
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "#e4e4e7",
  },
  emptyTitle: { fontSize: 15, fontWeight: "700", color: "#27272a", marginBottom: 6 },
  emptyBody: { fontSize: 13, color: "#52525b", lineHeight: 20 },
  card: {
    backgroundColor: "#fff",
    borderRadius: 14,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "#e4e4e7",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 4,
    elevation: 2,
  },
  cardSectionLabel: {
    fontSize: 11,
    fontWeight: "700",
    color: "#71717a",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 8,
  },
  verdictBanner: {
    borderRadius: 12,
    padding: 14,
    marginBottom: 8,
  },
  verdictBlock: { backgroundColor: "#fef2f2", borderWidth: 1, borderColor: "#fecaca" },
  verdictAllow: { backgroundColor: "#f0fdf4", borderWidth: 1, borderColor: "#bbf7d0" },
  verdictAppName: { fontSize: 17, fontWeight: "800", color: "#18181b" },
  verdictOutcome: { fontSize: 15, fontWeight: "700", color: "#3f3f46", marginTop: 4 },
  verdictHint: { fontSize: 12, color: "#52525b", marginTop: 8, lineHeight: 18 },
  cardSub: { fontSize: 12, color: "#71717a", marginBottom: 4 },
  subhead: { fontSize: 13, fontWeight: "700", color: "#4338ca", marginTop: 12, marginBottom: 4 },
  subheadConflict: { fontSize: 13, fontWeight: "700", color: "#9a3412", marginBottom: 8 },
  pathHint: { fontSize: 11, color: "#71717a", marginBottom: 10, lineHeight: 16 },
  stepRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 8,
    paddingVertical: 6,
    paddingHorizontal: 4,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: "#f4f4f5",
  },
  stepIndex: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: "#e4e4e7",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
    marginTop: 2,
  },
  stepIndexText: { fontSize: 12, fontWeight: "800", color: "#3f3f46" },
  stepBody: { flex: 1, paddingRight: 8 },
  stepDescription: { fontSize: 13, color: "#3f3f46", lineHeight: 20 },
  pill: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    alignSelf: "flex-start",
    marginTop: 2,
  },
  pillText: { fontSize: 11, fontWeight: "700" },
  pillTextBlock: { color: "#991b1b" },
  pillTextAllow: { color: "#166534" },
  pillTextContinue: { color: "#52525b" },
  pillTextPass: { color: "#1e40af" },
  pillBlock: { backgroundColor: "#fee2e2" },
  pillAllow: { backgroundColor: "#dcfce7" },
  pillContinue: { backgroundColor: "#f4f4f5" },
  pillPass: { backgroundColor: "#dbeafe" },
  conflictBox: {
    marginTop: 12,
    backgroundColor: "#fffbeb",
    borderRadius: 10,
    padding: 12,
    borderWidth: 1,
    borderColor: "#fde68a",
  },
  conflictInner: { marginBottom: 8 },
  conflictLabel: { fontSize: 11, fontWeight: "700", color: "#92400e", textTransform: "uppercase", marginBottom: 4 },
  conflictText: { fontSize: 13, color: "#78350f", lineHeight: 19 },
  conflictWinner: { fontSize: 13, color: "#78350f", marginTop: 8 },
  conflictWinnerEm: { fontWeight: "800" },
  conflictReason: { fontSize: 12, color: "#92400e", marginTop: 4, lineHeight: 17 },
  ruleRow: { flexDirection: "row", alignItems: "flex-start", marginBottom: 10, paddingLeft: 2 },
  ruleDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: "#6366f1",
    marginTop: 5,
    marginRight: 10,
  },
  ruleTextCol: { flex: 1 },
  ruleName: { fontSize: 14, fontWeight: "600", color: "#18181b" },
  ruleMeta: { fontSize: 12, color: "#71717a", marginTop: 2 },
  ruleIdMono: { fontFamily: "monospace", fontSize: 11, color: "#52525b" },
  disclaimer: {
    marginTop: 8,
    padding: 12,
    backgroundColor: "#fafafa",
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#e4e4e7",
  },
  disclaimerText: { fontSize: 11, color: "#71717a", lineHeight: 16 },
});
